// src/components/UPIModal.js — extracted from App.js
import React, { useState } from "react";
import { supabase } from "../lib/supabase";
import { UPI_ID, UPI_NAME } from "../constants";

// ─── UPI Payment Modal ────────────────────────────────────────────────────────
export function UPIModal({ amount, purpose, onSuccess, onClose, session }) {
  const [phase, setPhase]   = useState("idle"); // idle | loading | ready | manual | done | error
  const [txnId, setTxnId]   = useState("");
  const [err, setErr]       = useState("");
  const [cfOrderId, setCfOrderId] = useState("");

  const upiLink = `upi://pay?pa=${UPI_ID}&pn=${encodeURIComponent(UPI_NAME)}&am=${amount}&cu=INR&tn=${encodeURIComponent(purpose)}`;
  const qrUrl   = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=12&data=${encodeURIComponent(upiLink)}`;
  const u = session?.user;

  // Load Cashfree SDK once
  const loadCfSdk = () => new Promise((resolve, reject) => {
    if (window.Cashfree) { resolve(window.Cashfree); return; }
    const s = document.createElement("script");
    s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    s.onload = () => resolve(window.Cashfree);
    s.onerror = reject;
    document.head.appendChild(s);
  });

  const startCashfree = async () => {
    setPhase("loading"); setErr("");
    try {
      const orderId = "THC-" + Date.now();
      const { data, error } = await supabase.functions.invoke("cashfree-order", {
        body: {
          amount,
          orderId,
          customerName:  u?.name  || "THC Customer",
          customerEmail: u?.email || "customer@thc.watch",
          customerPhone: u?.phone || "9999999999",
        },
      });
      if (error || data?.error) throw new Error(data?.error || "Could not create payment order.");
      const { paymentSessionId, mode } = data;
      setCfOrderId(orderId);

      const Cashfree = await loadCfSdk();
      const cf = await Cashfree({ mode: mode === "production" ? "production" : "sandbox" });
      setPhase("ready");

      const result = await cf.checkout({ paymentSessionId, redirectTarget: "_modal" });
      if (result?.error) throw new Error(result.error.message || "Payment failed.");
      if (result?.paymentDetails || result?.redirect === false) {
        setTxnId(orderId);
        setPhase("done");
        setTimeout(() => onSuccess(orderId), 1200);
      }
    } catch (e) {
      setErr(e.message || "Payment could not be completed.");
      setPhase("error");
    }
  };

  const handleManualVerify = () => {
    if (txnId.trim().length < 6) { setErr("Enter a valid UTR / Transaction ID (min 6 chars)."); return; }
    setPhase("done");
    setTimeout(() => onSuccess(txnId.trim()), 1200);
  };

  return (
    <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="modal fade-in">
        <button onClick={onClose} style={{position:"absolute",top:16,right:18,background:"none",fontSize:20,color:"var(--ink3)",lineHeight:1}}>×</button>

        {phase === "done" ? (
          <div className="fade-in" style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{fontSize:52,marginBottom:12}}>✓</div>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:26,marginBottom:8}}>Payment confirmed.</div>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:11,color:"var(--ink3)",marginBottom:4}}>{txnId}</div>
            <div style={{fontSize:13,color:"var(--ink3)"}}>Placing your order…</div>
          </div>
        ) : (
          <div className="fade-in">
            <div style={{marginBottom:20}}>
              <div className="sl" style={{marginBottom:4}}>Pay to complete your request</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:28,fontWeight:700}}>₹{amount.toLocaleString("en-IN")}</div>
              <div style={{fontSize:12,color:"var(--ink3)",marginTop:3}}>{purpose}</div>
            </div>

            {/* Cashfree primary CTA */}
            {phase !== "manual" && (
              <button
                className="btn-ink"
                style={{width:"100%",padding:14,fontSize:14,marginBottom:12,opacity:phase==="loading"?0.7:1}}
                onClick={startCashfree}
                disabled={phase==="loading"||phase==="ready"}
              >
                {phase==="loading"?"Setting up payment…":phase==="ready"?"Opening checkout…":"Pay ₹"+amount.toLocaleString("en-IN")+" →"}
              </button>
            )}

            {(phase==="error") && (
              <div style={{color:"var(--red)",fontSize:12,marginBottom:12,fontFamily:"'DM Mono',monospace"}}>{err}</div>
            )}

            {/* Divider */}
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
              <div style={{flex:1,height:1,background:"var(--bd)"}}/>
              <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em"}}>OR PAY MANUALLY</span>
              <div style={{flex:1,height:1,background:"var(--bd)"}}/>
            </div>

            {/* Manual UPI fallback */}
            <div style={{background:"var(--bg2)",border:"1.5px solid var(--bd)",borderRadius:12,padding:"16px",textAlign:"center",marginBottom:14}}>
              <img src={qrUrl} alt="UPI QR" style={{width:150,height:150,display:"block",margin:"0 auto 10px",borderRadius:8}}/>
              <div style={{fontFamily:"'DM Mono',monospace",fontSize:13,fontWeight:600,letterSpacing:"0.06em",color:"var(--ink)",marginBottom:3}}>{UPI_ID}</div>
              <div style={{fontSize:11,color:"var(--ink3)"}}>GPay · PhonePe · Paytm · any UPI app</div>
            </div>
            <a href={upiLink} style={{display:"block",marginBottom:14,textDecoration:"none"}}>
              <button className="btn-gold" style={{width:"100%",padding:11,fontSize:13}}>📱 Open in UPI App</button>
            </a>
            <div style={{borderTop:"1px solid var(--bd)",paddingTop:14}}>
              <div className="sl" style={{marginBottom:6}}>After paying manually, enter your UTR</div>
              <input value={txnId} onChange={e=>{setTxnId(e.target.value);setErr("");}} placeholder="12-digit UTR or Transaction ID" style={{marginBottom:8,fontFamily:"'DM Mono',monospace",letterSpacing:"0.06em"}} type="text" inputMode="numeric" autoComplete="off"/>
              {err && phase==="idle" && <div style={{color:"var(--red)",fontSize:12,marginBottom:8}}>{err}</div>}
              <button className="btn-green" style={{width:"100%",padding:12,fontSize:13}} onClick={handleManualVerify}>Confirm manual payment →</button>
            </div>
            <div style={{textAlign:"center",marginTop:12}}>
              <button onClick={()=>onSuccess("TEST-"+Date.now())} style={{background:"none",color:"var(--ink3)",fontSize:11,textDecoration:"underline",fontFamily:"'DM Mono',monospace"}}>Skip for testing →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Notification helpers ─────────────────────────────────────────────────────
